<script setup>
import { ref, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import { $bus } from '@/common/events'
import { dialog } from '@/common/dialog'
import { $router } from '@/router'
import { useTitle } from '@/composables/title'
import { useMenu, useSubmenu, useTopBar } from '@/composables/menu'
import { useSessionTimer } from '@/composables/session'
import { LogService } from '@/services/LogService.js'
import { AuthService } from '@/services/AuthService.js'
import { ConfigService } from '@/services/ConfigService.js'
import { SetupService } from '@/services/SetupService.js'
import KeyboardNumpad from '@/components/KeyboardNumpad.vue'
import LanguageSelector from '@/components/LanguageSelector.vue'
import DebugFooter from '@/components/Maintenance/DebugFooter.vue'

const logService = new LogService();
const authService = new AuthService();
const configService = new ConfigService();
const setupService = new SetupService();

const screen = ref("input"); //input, loading, error
const isLoading = ref(false);

const code1 = ref("");
const code2 = ref("");
const code3 = ref("");
const code4 = ref("");

const route = useRoute();

onMounted(async () => {
  //Hide menu
  useTitle("Namestitev");
  useMenu(false);
  useSubmenu(false);
  useTopBar(false);

  //Avoid session timeout during setup procedure
  useSessionTimer(false);
});

async function submit() {
  try {
    isLoading.value = true;

    let code = `${code1.value}-${code2.value}-${code3.value}-${code4.value}`;

    let result = await setupService.start({ code });
    if (result.error) {
      throw new Error(result.error);
    }

    if (result.status == "deploying") {
      screen.value = "loading";
    }
  }
  catch (err) {
    dialog(err);
  }
  finally {
    isLoading.value = false;
  }
}

function nextInput(e) {
  let id = e.target.id;
  let num = parseInt(id.replace("code", ""));

  console.log(e);

  let code = null;
  switch (num) {
    case 1: code = code1; break;
    case 2: code = code2; break;
    case 3: code = code3; break;
    case 4: code = code4; break;
  }

  if (e.keyCode == 8) { //Backspace
    if (code.value == "" && num > 1) {
      let prev = "code" + (num - 1);
      document.getElementById(prev).focus();
    }
  }
  else if (e.keyCode == 9) { //Tab
  }
  else if (e.keyCode == 13) { //Enter
    submit();
    e.preventDefault();
    e.stopPropagation();
  }
  else if ((e.keyCode >= 65 && e.keyCode <= 90) || e.keyCode >= 48 && e.keyCode <= 57) { //A-Z or 0-9
    if (code.value.length < 4) {
      e.target.value += ("" + e.key).toUpperCase();
    }
    else if (num < 4) {
      let next = "code" + (num + 1);
      document.getElementById(next).focus();
    }
    e.preventDefault();
    e.stopPropagation();
  }
  else {
    e.preventDefault();
    e.stopPropagation();
  }

  code.value = e.target.value;
}
</script>

<template>
  <!-- Prompt for setup code -->
  <div class="grid h-screen place-items-center dark:bg-gray-900 dark:text-white" v-if="screen == 'input'">
    <div>
      <div class="mb-6">
        <label for="code" class="block mb-2 text-sm font-medium text-gray-900 dark:text-white">{{ $t('setup.enter_code') }}</label>
        <div class="mb-6 w-full">
          <input type="text" id="code1" class="textbox" required @keydown="nextInput" :disabled="isLoading">
          <span class="mx-2">&hyphen;</span>
          <input type="text" id="code2" class="textbox" required @keydown="nextInput" :disabled="isLoading">
          <span class="mx-2">&hyphen;</span>
          <input type="text" id="code3" class="textbox" required @keydown="nextInput" :disabled="isLoading">
          <span class="mx-2">&hyphen;</span>
          <input type="text" id="code4" class="textbox" required @keydown="nextInput" :disabled="isLoading">
          <button type="button" @click="submit" class="ml-3 button" style="width: 90px;" v-if="!isLoading">{{ $t('setup.submit') }}</button>
          <button type="button" class="ml-3 button" style="width: 90px; position: relative; top: 5px;" disabled v-if="isLoading"><Spinner size="5" style="margin: 0 auto;" /></button>
        </div>
      </div>
      
    </div>
  </div>

  <!-- Synchronization in progress -->
  <div role="status" class="grid h-screen place-items-center dark:bg-gray-900 dark:text-white" v-if="screen == 'loading'">
    <div>
      <Spinner size="10" style="margin: 0 auto;" />
      <div class="mt-5">{{ $t('setup.synchronizing') }}</div>
    </div>
  </div>

  <DebugFooter></DebugFooter>
</template>

<style scoped>
.textbox {
  @apply bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 w-24 p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500;
}

.button {
  @apply text-white bg-blue-700 hover:bg-blue-800 focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium rounded-lg text-sm w-full sm:w-auto px-5 py-2.5 text-center dark:bg-blue-600 dark:hover:bg-blue-700 dark:focus:ring-blue-800;
}
</style>
